import Immutable from 'immutable';
import { createSelector } from 'reselect';
import { selectCurrentCoursePath } from '../Courses/selectors';
import { id } from '../../web/utilities/functions';
import { usersByProfileNameSelector } from '../Users/selectors';
import { NO_CERTIFICATE, CertificationTypes } from '../Certification/constants';

export const selectCourseAdminUI = (state) =>
  state.CourseAdminUI || Immutable.Map();

const accessEmails = (courseAdminUI, coursePath) => {
  return (
    !!courseAdminUI && courseAdminUI.getIn(['accessEmails', coursePath], false)
  );
};

export const makeSelectCourseCanAccessEmails = (coursePath) =>
  createSelector([selectCourseAdminUI], (courseAdminUI) =>
    accessEmails(courseAdminUI, coursePath)
  );

export const selectCurrentCourseCanAccessEmails = createSelector(
  [selectCourseAdminUI, selectCurrentCoursePath],
  accessEmails
);

export const selectCurrentCourseLandingPageData = createSelector(
  [selectCourseAdminUI, selectCurrentCoursePath],
  (courseAdminUI, coursePath) => {
    return courseAdminUI
      ? courseAdminUI.getIn(
          ['landingPage', coursePath, 'data'],
          Immutable.Map()
        )
      : Immutable.Map();
  }
);

export const selectCurrentCourseLandingPageTeamMembers = createSelector(
  [selectCurrentCourseLandingPageData, usersByProfileNameSelector],
  (landingPageData, usersByProfileName) => {
    const teamMembers = landingPageData.get('teamMembers', Immutable.List());
    return teamMembers
      .map((profileName) => usersByProfileName.get(profileName))
      .filter(id);
  }
);

export const selectCurrentCourseLandingPageDataIsSaving = createSelector(
  [selectCourseAdminUI, selectCurrentCoursePath],
  (courseAdminUI, coursePath) => {
    return courseAdminUI
      ? courseAdminUI.getIn(['landingPage', coursePath, 'isSaving'], false)
      : false;
  }
);

export const selectCourseCopyIsLoading = createSelector(
  [selectCourseAdminUI, selectCurrentCoursePath],
  (courseAdminUI, coursePath) => {
    return courseAdminUI
      ? courseAdminUI.getIn(['courseCopy', coursePath, 'isLoading'], false)
      : false;
  }
);

export const selectShowCourseCopySuccessModal = createSelector(
  [selectCourseAdminUI, selectCurrentCoursePath],
  (courseAdminUI, coursePath) => {
    return courseAdminUI
      ? courseAdminUI.getIn(
          ['courseCopy', coursePath, 'showSuccessModal'],
          false
        )
      : false;
  }
);

export const selectShowCourseCopyErrorModal = createSelector(
  [selectCourseAdminUI, selectCurrentCoursePath],
  (courseAdminUI, coursePath) => {
    return courseAdminUI
      ? courseAdminUI.getIn(['courseCopy', coursePath, 'showErrorModal'], false)
      : false;
  }
);

export const selectCopiedCoursePath = createSelector(
  [selectCourseAdminUI, selectCurrentCoursePath],
  (courseAdminUI, coursePath) => {
    return courseAdminUI
      ? courseAdminUI.getIn(['courseCopy', coursePath, 'newCourse', 'path'], '')
      : '';
  }
);

const selectCurrentCourseCertification = createSelector(
  [selectCourseAdminUI, selectCurrentCoursePath],
  (courseAdminUI, coursePath) =>
    courseAdminUI.getIn(['certification', coursePath], Immutable.Map())
);

export const selectCertificateOptions = createSelector(
  selectCurrentCourseCertification,
  (certification) => certification.get('certificateOptions', Immutable.Map())
);

export const selectAvailableCertificationTypes = createSelector(
  selectCurrentCourseCertification,
  (certification) => {
    const allowedTypes = certification.get(
      'certificationTypes',
      Immutable.List()
    );
    return CertificationTypes.filter(
      (type) => type === NO_CERTIFICATE || allowedTypes.includes(type)
    );
  }
);

export const selectCurrencyOptions = createSelector(
  selectCurrentCourseCertification,
  (certification) => {
    return certification.get('currencyOptions', Immutable.List());
  }
);

export const selectCertificationData = createSelector(
  [selectCurrentCourseCertification, selectCertificateOptions],

  (certification, certificateOptions) => {
    let certificationData = Immutable.Map({
      type: certification.get('type', NO_CERTIFICATE),
      code: certification.get('code', ''),
      data: certification.get('data', Immutable.Map()),
      errors: certification.get('errors', Immutable.Map()),
    });

    // As of BUFF-3353 We are using the institution logo
    // from the certificate appearance set up
    // as the certified by logo for non-ASQA certifications
    certificationData = certificationData.setIn(
      ['data', 'certifiedByLogo'],
      certificateOptions.get('institutionLogoImageUrl', '')
    );
    return certificationData;
  }
);

export const selectCertificationIsSaving = createSelector(
  selectCurrentCourseCertification,
  (certification) => certification.get('isSaving', false)
);

export const selectCertificationIsPristine = createSelector(
  selectCurrentCourseCertification,
  (certification) => certification.get('pristine', false)
);

export const selectAccredibleDesigns = createSelector(
  selectCurrentCourseCertification,
  (certification) => certification.get('accredibleDesigns', null)
);

export const selectStripeConnectStatus = createSelector(
  [selectCourseAdminUI, selectCurrentCoursePath],
  (courseAdminUI, currentPath) =>
    courseAdminUI.getIn(
      ['stripeConnectStatus', currentPath],
      'no_revenue_share'
    )
);
